import { action, computed, makeObservable, observable } from 'mobx';

import { getRegisteredClass } from '../SatCoreRegistry';

import { AVATAR_PICKER_LISTS, MAX_THRESHOLD_PAGE_SIZE } from '../constants';

import { getSessionStorageItem, removeSessionStorageItem, setSessionStorageItem } from '../utils/session';

import {
  INSTITUTION_DROPDOWN_MODE,
  ORIGINAL_SELECTED_DROPDOWN_INSTITUTION_ID_OBSERVABLE_OBJ
} from '../services/UserInstitutionDropdownConstants';

import Auth from './AuthManager';

export const USER_ENDPOINTS = {
  ACTIVATE_SHOPIFY_SIGNUP_CODE: '/api/activateShopifySignupCode',
  ACTIVATE_USER_INVITATION: '/api/activateUserInvitation',
  ADD_USER_OPT_OUT: '/api/addUserOptOut',
  CHANGE_FORGOTTEN_PASSWORD: '/api/changePassword',
  CHECK_USER_OPT_OUT: '/api/checkUserOptOut',
  CREATE_USER_INSTITUTION: '/api/addUserInstitution',
  GENERATE_PASSWORD: '/api/generatePassword',
  GENERATE_USERNAME: '/api/generateUsername',
  GET_USER_INVITATION: '/api/getUserInvitation',
  GET_USE_ASSIGNMENT_REACT_PLAYER: '/api/validateReactForActivity',
  PING_LOGGED_IN: '/api/ping',
  REGISTER_DEMOLINK_USER: '/api/generateDemoLinkUser',
  REGISTER_ECOMMERCE_USER: '/api/generateEcommerceUser',
  REMOVE_USER_INSTITUTION: '/api/removeUserInstitution',
  SAVE_USER_DEFAULT_AVATAR: '/api/saveUserDefaultAvatar',
  SIGNUP_DISTRICT_ADMIN: '/api/signupDistrictAdmin',
  SIGNUP_SCHOOL_ADMIN: '/api/signupSchoolAdmin',
  SIGNUP_STUDENT: '/api/signupStudent',
  SIGNUP_TEACHER: '/api/signupTeacher',
  UPDATE_PROFILE_PASSWORD: '/api/updatePassword',
  UPDATE_PROFILE_PASSWORD_STUDENT: '/api/updatePassword',
  VIEW_INSTITUTIONS_FOR_DROPDOWN: '/api/viewInstitutionsForDropDown',
  VIEW_SCHOOL_INSTITUTIONS_FOR_DROPDOWN: '/api/viewSchoolInstitutionsForDropDown',
  VIEW_SINGULAR_INSTITUTION: '/api/viewInstitutionForSatellite',
  VIEW_STUDENT_BY_FIRST_AND_LAST_NAME: '/api/viewStudentByFirstAndLastName',
  VIEW_USER_INSTITUTIONS: '/api/viewUserInstitutionsForSatellite'
};

export const ERROR_MESSAGES = {
  ACCESS_CODE_INVALID: 'This access code does not work. Please check your code and try again.',
  GENERAL: 'Something has gone wrong with your registration.  ' +
    'Please check your information and try again.  If you think everything is correct, please contact your administrator.',
  PASSWORDS_DO_NOT_MATCH: 'Passwords do not match!',
  PASSWORD_LENGTH: 'Password must be at least 8 characters and cannot be all spaces.',
  STUDENT_USERNAME_INVALID_EMAIL: 'Student username cannot be an email address.',
  USERNAME_EXISTS: 'There is already a user with the Username/email you entered.  Try again using a different username.',
  USER_EXISTS: 'There is already a user in the system with that username/email. Please try another username/email.',
  USER_EXISTS_NO_EMAIL: 'There is already a user in the system with that username. Please try another username.',
  VALID_EMAIL_FORMAT: 'Please enter a valid email format. (name@example.com)',
};

export class UserManager {
  districtAdminId = 'district_admin_user';
  districtAssessmentAdminId = 'district_assessment_admin';
  districtProductAdminId = 'district_product_admin';
  experimentalAccessId = 'experimental_access';
  manageLearnosityScoresId = 'manage_learnosity_scores';
  manageUploadedFilesId = 'manage_uploaded_files';
  publisherAdminId = 'manage_publisher_products';
  publisherUserId = 'publisher_user';
  reactPlayerPermissionId = 'view_react_player';
  satelliteAdminId = 'satellite_admin_user';
  satelliteBetaAccessUserId = 'satellite_beta_access';
  schoolAdminId = 'school_admin_user';
  schoolAssessmentAdminId = 'school_assessment_admin';
  schoolProductAdminId = 'school_product_admin';
  studentPermissionId = 'student_user';
  teacherPermissionId = 'teacher_user';
  viewAdminLibraryItemBank = 'view_admin_library_item_bank';
  viewAdminLibraryResourceBank = 'view_admin_library_resource_bank';
  viewEngagementDataId = 'view_engagement_data';
  viewSatelliteClasses = 'view_satellite_admin_classes';
  viewSatelliteContent = 'view_satellite_content';
  viewSatelliteLive = 'view_satellite_admin_live';
  viewSatelliteReports = 'view_satellite_admin_reports';
  viewSatelliteSchools = 'view_satellite_admin_schools';
  viewSatelliteUsers = 'view_satellite_admin_users';
  viewTeacherLibraryItemBank = 'view_teacher_library_item_bank';
  viewTeacherLibraryResourceBank = 'view_teacher_library_resource_bank';
  viewWhiteLabelStandardsReportsId = 'view_white_label_standards_reports';

  @observable allowLplAdmin = false; // set via `setAllowLplAdmin(true)` flag in satellite index.js (e.g. c2c-empowered)
  @observable allowSwitchRole = true; // set via `setAllowSwitchRole(true|false)` flag in satellite index.js

  // TODO remove // @observable accommodationIds = {};
  @observable activePermissionId = '';
  @observable permissionIds = [];

  /**
   * Permission ID used when a publisher user is previewing content as a different role.
   * When set, this grants preview-mode teacher/student permissions while maintaining
   * the user's actual activePermissionId (e.g. publisher_user).
   */
  @observable activePreviewPermissionId = null;

  @observable authKey = '';
  @observable displayName = '';
  @observable firstName = '';
  @observable fullProfileImageUrl = '';
  @observable institutionImage = null;
  @observable institutionState = '';
  @observable institutionStateId = '';
  @observable isSsoUser = false;
  @observable lastName = '';
  @observable loaded = false;
  @observable profileImageUrl = '';
  @observable user = null;
  @observable userId = '';
  @observable username = '';
  @observable pingErrorCount = 0;

  @observable hideClassMenuDropdown = false;

  /** **primary** `institutionId` but a user may have multiple `institutionIds` */
  @observable institutionId = null;
  @observable selectedDropdownInstitutionIdObservableObj = {
    addClassroomMode: getSessionStorageItem('selectedDropdownInstitutionIdStr') || 'allInstitutions',
    selectClassroomMode: getSessionStorageItem('selectedDropdownInstitutionIdStr') || 'allInstitutions',
    selectReportClassroomMode: getSessionStorageItem('selectedDropdownInstitutionIdStr') || 'allInstitutions'
  };

  /** allows the ability to provide computed arrays: `institutionIds` and `institutions` */
  @observable institutionsMap = new Map();

  @observable accessCodeResult = false;
  @observable emailResult = false;
  @observable institutionName = '';
  @observable institutionTimezoneid = '';
  @observable clientPrefix = '';
  @observable isCoTeacher = false;
  @observable enabled = false;
  @observable timezoneId = '';
  @observable creationDate = '';
  @observable lastLogin = '';
  @observable loginAttempts = '';
  @observable textHelpEnabled = false;
  @observable publisherTextHelpActive = false;
  @observable showTextHelpForAll = false;
  @observable publisherId = '';
  @observable isImpersonatorAdmin = false;
  @observable allowStudentUsernamesAsEmail = true;
  @observable allowViewSatelliteProductTab = true;
  @observable allowTeacherLibraryContentView = true;

  @observable isUserProfileReadOnly = false;
  @observable hideUserInstitutionsListFromProfile = false;
  @observable isAlwaysReadOnlyUserInstitutionsListFromProfile = false;

  @observable avatarPickerFacultyName = 'AVATAR_PICKER_DEFAULT_LIST';
  @observable avatarPickerStudentName = 'AVATAR_PICKER_DEFAULT_STUDENT_LIST';

  @observable restrictAvatar = false;
  @observable restrictAvatarForAllUserTypes = false;

  @observable customEndpointsForUser = {}; // can be overridden via satellite index.js (or satelliteLayout.json if dynamic satellite)

  // set via `setHideReportsTab(true|false)` flag in satellite index.js (or satelliteLayout.json if dynamic satellite)
  // Can be set when we hide reports tab in topnav to also prevent users from other ways of accessing reports page
  @observable hideReportsTab = false;
  
  constructor() {
    makeObservable(this);
  }

  @action clear() {
    this.accessCodeResult = false;
    this.authKey = '';
    this.clearActivePermissionId();
    this.clearActivePreviewPermissionId();
    this.creationDate = '';
    this.displayName = '';
    this.emailResult = false;
    this.enabled = false;
    this.firstName = '';
    this.fullProfileImageUrl = '';
    this.hideClassMenuDropdown = false;
    this.institutionId = null;
    this.institutionImage = null;
    this.institutionName = '';
    this.institutionState = '';
    this.institutionStateId = '';
    this.institutionTimezoneid = '';
    this.institutionsMap = new Map();
    this.isCoTeacher = false;
    this.isImpersonatorAdmin = false;
    this.lastLogin = '';
    this.lastName = '';
    this.loaded = false;
    this.loginAttempts = '';
    this.permissionIds = [];
    this.profileImageUrl = '';
    this.publisherId = '';
    this.selectedDropdownInstitutionIdObservableObj = { ...ORIGINAL_SELECTED_DROPDOWN_INSTITUTION_ID_OBSERVABLE_OBJ };
    this.timezoneId = '';
    this.userId = '';
    this.username = '';
  }

  @action clearSession() {
    removeSessionStorageItem('selectedDropdownInstitutionIdStr');
  }

  @action setIsImpersonatorAdmin(toggle) {
    this.isImpersonatorAdmin = toggle;
  }

  @action getIsImpersonatorAdmin() {
    return this.isImpersonatorAdmin;
  }

  @action setLoaded(toggle) {
    this.loaded = toggle;
  }

  @action setAllowLplAdmin = (allowLplAdmin) => {
    this.allowLplAdmin = allowLplAdmin;
  }

  @action setAllowSwitchRole = (allowSwitchRole) => {
    this.allowSwitchRole = allowSwitchRole;
  }

  @action setActivePermissionId = (permissionId) => {
    this.activePermissionId = permissionId;
    if (permissionId) {
      setSessionStorageItem('activePermissionId', permissionId);
    }
  }

  @action clearActivePermissionId = () => {
    this.activePermissionId = null;
    removeSessionStorageItem('activePermissionId');
  }

  @action setActivePreviewPermissionId = (permissionId) => {
    this.activePreviewPermissionId = permissionId;
    if (permissionId) {
      setSessionStorageItem('activePreviewPermissionId', permissionId);
    } else {
      removeSessionStorageItem('activePreviewPermissionId');
    }
  }

  @action clearActivePreviewPermissionId = () => {
    this.activePreviewPermissionId = null;
    removeSessionStorageItem('activePreviewPermissionId');
  }

  @action setIsCoTeacher(toggle) {
    this.isCoTeacher = toggle;
  }

  @action setHideClassMenuDropdown(hide) {
    this.hideClassMenuDropdown = hide;
  }

  @action setClientPrefix(prefix) {
    this.clientPrefix = prefix;
  }

  @action setAccessCodeResult(result) {
    this.accessCodeResult = result;
  }

  @action setEmailResult(result) {
    this.emailResult = result;
  }

  @action setPublisherTextHelpActive(active) {
    this.publisherTextHelpActive = active;
  }

  @action setShowTextHelpForAll(show) {
    this.showTextHelpForAll = show;
  }

  @action setTextHelpEnabled(enabled) {
    this.textHelpEnabled = enabled;
  }

  @action setUser = (user, institutions) => {
    this.user = user;

    const now = new Date();
    this.firstName = user.firstName;
    this.lastName = user.lastName;
    this.username = user.username;
    this.displayName = user.displayName;

    // TODO remove // this.addUserInstitutionsToMap(institutions);

    this.permissionIds = user.permissionIds;

    let activePermissionId;
    if (this.hasPublisherPermission) {
      this.setActivePermissionId(this.publisherUserId);
    } else {
      const UserService = getRegisteredClass('UserService');
      const availableRoles = UserService.getAvailableRoles();

      // if only one role, we can assume that is the current role of the user
      // otherwise, we will set active role to sessionStorage activePermissionId if it exists
      // if activePermissionId is null, MainView will redirect the user to RoleSwitcherView (to choose an available role)
      if (!this.allowSwitchRole || availableRoles.length === 1) {
        // eslint-disable-next-line prefer-destructuring
        activePermissionId = availableRoles[0];
        this.setActivePermissionId(activePermissionId);
      } else {
        activePermissionId = getSessionStorageItem('activePermissionId');
        this.setActivePermissionId(activePermissionId);
      }
    }

    // Restore publisher preview permission from sessionStorage if applicable
    // (e.g. for page refreshes during publisher preview)
    const storedPreviewPermissionId = getSessionStorageItem('activePreviewPermissionId');
    if (storedPreviewPermissionId) {
      this.setActivePreviewPermissionId(storedPreviewPermissionId);
    }

    // TODO remove // this.accommodationIds = user.accommodationIds;
    this.profileImageUrl = user.profileImageUrl;
    this.fullProfileImageUrl = `${user.fullProfileImageUrl}&authKey=${user.authKey}&rn=${now.getTime()}`;
    this.userId = user.userId;
    this.authKey = user.authKey;
    Auth.setAuthKey(this.authKey);
    this.loaded = true;

    this.isSsoUser = user.isSsoUser && user.isSsoUser !== 'false';

    if (institutions) {
      this.addUserInstitutionsToMap(institutions);
    }

    // set 'primary' institutionId
    // if district admin, FE will treat 'primary' institutionId as the first districtId it finds
    this.institutionId = activePermissionId === this.districtAdminId && institutions ?
      institutions.find((institution) => {
        return institution?.district || institution?.type?.toLowerCase() === 'district';
      })?.id || user.institutionId : user.institutionId;

    this.institutionState = user.institutionState;
    this.institutionStateId = user.institutionStateId;
    this.institutionName = user.institutionName;
    this.institutionTimezoneid = user.institutionTimezoneid;

    this.enabled = user.enabled;
    this.timezoneId = user.timezoneId;
    this.creationDate = user.creationDate;
    this.lastLogin = user.lastLogin;
    this.loginAttempts = user.loginAttempts;
    this.publisherId = user.publisherId;
  }

  createFullAuthUrl = (url) => {
    const now = new Date();
    return `${url}&authKey=${this.authKey}&rn=${now.getTime()}`;
  }

  @action setUserEnabled(enabled) {
    this.enabled = enabled;
  }

  @action setFirstName(name) {
    this.firstName = name;
  }

  @action setLastName(name) {
    this.lastName = name;
  }

  @action setAllowViewSatelliteProductTab(allow) {
    this.allowViewSatelliteProductTab = allow;
  }

  @action setAllowTeacherLibraryContentView(allow) {
    this.allowTeacherLibraryContentView = allow;
  }

  @action setHideUserInstitutionsListFromProfile(hideUserInstitutionsListFromProfile) {
    this.hideUserInstitutionsListFromProfile = hideUserInstitutionsListFromProfile;
  }

  @action setIsAlwaysReadOnlyUserInstitutionsListFromProfile(isAlwaysReadOnlyUserInstitutionsListFromProfile) {
    this.isAlwaysReadOnlyUserInstitutionsListFromProfile = isAlwaysReadOnlyUserInstitutionsListFromProfile;
  }

  @action setIsUserProfileReadOnly(isUserProfileReadOnly) {
    this.isUserProfileReadOnly = isUserProfileReadOnly;
  }

  @action setAvatarPickerFacultyName = (avatarPickerFacultyName) => {
    if (typeof avatarPickerFacultyName === 'string' && avatarPickerFacultyName in AVATAR_PICKER_LISTS) {
      this.avatarPickerFacultyName = avatarPickerFacultyName;
    }
  }

  @action setAvatarPickerStudentName = (avatarPickerStudentName) => {
    if (typeof avatarPickerStudentName === 'string' && avatarPickerStudentName in AVATAR_PICKER_LISTS) {
      this.avatarPickerStudentName = avatarPickerStudentName;
    }
  }

  @action setRestrictAvatar(restrict) {
    this.restrictAvatar = restrict;
  }

  @action setRestrictAvatarForAllUserTypes(restrict) {
    this.restrictAvatarForAllUserTypes = restrict;
  }

  @action setHideReportsTab(hideReportsTab) {
    this.hideReportsTab = hideReportsTab;
  }

  @action async getTempStudentCode() {
    try {
      const response = await Auth.fetch(`${Auth.ecms}/api/generateRandomCode`, {
        method: 'POST'
      });

      if (response.tempRandomCode) {
        return response.tempRandomCode;
      }
    } catch (e) {
      console.error(e);
    }
  }

  @action async generateUsername(firstName, lastName) {
    try {
      const response = await Auth.fetch(`${Auth.ecms}${USER_ENDPOINTS.GENERATE_USERNAME}`, {
        method: 'POST',
        body: {
          firstName,
          lastName
        }
      });

      if (response.status === 'SUCCESS' && response.availableUsername) {
        return response.availableUsername;
      }
      return null;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  @action async generatePassword(passwordLength = 8) {
    try {
      const response = await Auth.fetch(`${Auth.ecms}${USER_ENDPOINTS.GENERATE_PASSWORD}`, {
        method: 'POST',
        body: {
          passwordLength
        }
      });

      if (response.status === 'SUCCESS' && response.tempPassword) {
        return response.tempPassword;
      }
      return null;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  @action async viewStudentByFirstAndLastName(classroomId, firstName, lastName) {
    try {
      const params = new URLSearchParams({
        classroomId,
        firstName,
        lastName
      });
      const response = await Auth.fetch(
        `${Auth.ecms}${USER_ENDPOINTS.VIEW_STUDENT_BY_FIRST_AND_LAST_NAME}?${params.toString()}`,
        { method: 'GET' }
      );

      if (response.status === 'SUCCESS' && response.data && response.data.length > 0) {
        return response.data[0];
      }
      return null;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  @action setAllowStudentUsernamesAsEmail(allowStudentUsernamesAsEmail) {
    this.allowStudentUsernamesAsEmail = allowStudentUsernamesAsEmail;
  }

  async fetchUser(userId) {
    try {
      const response = await Auth.fetch(`${Auth.ecms}/api/getUser?userId=${userId}`, {
        method: 'GET'
      });

      if (response.status === 'SUCCESS') {
        if (response.data && response.data.length > 0) {
          return response.data[0];
        }
      }
      return null;
    } catch (e) {
      console.error(e);
    }
  }

  async fetchMyUserOptions() {
    // if we know, don't do it again.
    // if (this.publisherTextHelpActive !== null) {
    //  return;
    // }
    try {
      const response = await Auth.fetch(`${Auth.ecms}/api/getMyUserOptions`, {
        method: 'GET'
      });
      if (response.status === 'SUCCESS') {
        this.setPublisherTextHelpActive(response.textHelpActive);
        this.setTextHelpEnabled(response.textHelpEnabled);
      }
      return null;
    } catch (e) {
      console.error(e);
    }
  }

  @action fetchUseAssignmentReactPlayer = async (activityId) => {
    try {
      let apiUrl = `${Auth.ecms}${USER_ENDPOINTS.GET_USE_ASSIGNMENT_REACT_PLAYER}`;
      apiUrl += `?id=${activityId}`;
      const response = await Auth.fetch(apiUrl, { method: 'GET' });
      if (response?.status === 'SUCCESS' && response?.isReact) {
        return response?.isReact;
      }
      return false;
    } catch (error) {
      console.error(error);
    }
  }

  @action checkUser = async (authKey = undefined) => {
    const user = authKey ? await Auth.logInWithAuthKey(authKey) : await Auth.checkUser();
    if (user) {
      const primaryInstitution = await this.fetchSingularUserInstitution(user.institutionId);
      const institutions = await this.fetchUserInstitutions(user);
      this.institutionsMap.clear();
      this.addUserInstitutionsToMap([...institutions, primaryInstitution]);
      this.setUser(user, institutions);
      return true;
    }
    return false;
  }

  @action checkUserOptOut = async (optOutKey) => {
    try {
      const response = await Auth.fetch(`${Auth.ecms}${USER_ENDPOINTS.CHECK_USER_OPT_OUT}`, {
        body: { optOutKey },
        method: 'POST'
      });
      return response?.status === 'SUCCESS' && response?.optOut === true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  @action addUserOptOut = async (optOutKey) => {
    try {
      const response = await Auth.fetch(`${Auth.ecms}${USER_ENDPOINTS.ADD_USER_OPT_OUT}`, {
        method: 'POST',
        body: { optOutKey }
      });
      return response?.status === 'SUCCESS';
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  @action impersonatorLogIn = async (authKey) => {
    const result = await this.authKeyLogIn(authKey);
    if (result) {
      this.setIsImpersonatorAdmin(true);
      return true;
    }
    return false;
  }

  @action cleverLogIn = async (authKey) => this.authKeyLogIn(authKey)

  @action authKeyLogIn = async (authKey) => {
    try {
      const user = await Auth.logInWithAuthKey(authKey);
      if (user && user.status === 'SUCCESS') {
        this.setUser(user);
        return true;
      }
      return false;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  @action publisherLogIn = async (authKey) => {
    try {
      const user = await Auth.logInWithAuthKey(authKey);
      if (user && user.status === 'SUCCESS') {
        this.setUser(user);
        return true;
      }
      return false;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  // ============================================================================
  // USER INSTITUTIONS
  // ============================================================================

  /**
   * Fetch all institutions a user belongs to.
   * @param {Object} user
   * @param {{ userId?: string, pageSize?: number, skip?: number }} [options]
   * @returns {Promise<Array|undefined>} the user institutions
   */
  @action fetchUserInstitutions = async (user, {
    userId = null,
    pageSize = MAX_THRESHOLD_PAGE_SIZE,
    skip = 0
  } = {}) => {
    try {
      userId = userId || user?.userId || user?.id || this.userId;
      let apiUrl = `${Auth.ecms}${USER_ENDPOINTS.VIEW_USER_INSTITUTIONS}`;
      apiUrl += `?userId=${userId}`;
      apiUrl += `&pageSize=${pageSize}`;
      apiUrl += `&skip=${skip}`;
      const response = await Auth.fetch(apiUrl, { method: 'GET' });
      if (response?.status === 'SUCCESS') {
        return response.data || [];
      }
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  /**
   * Fetch a single institution a user belongs to, by institution id.
   * @param {string} institutionId
   * @returns {Promise<Object|undefined>} the institution
   */
  @action fetchSingularUserInstitution = async (institutionId) => {
    try {
      let apiUrl = `${Auth.ecms}${USER_ENDPOINTS.VIEW_SINGULAR_INSTITUTION}`;
      apiUrl += `?institutionId=${institutionId}`;
      const response = await Auth.fetch(apiUrl, { method: 'GET' });
      if (response?.status === 'SUCCESS' && response.institutions) {
        return response.institutions[0];
      }
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Add an institution to a user.
   * @param {{ institutionId: string, userId: string }} args
   * @returns {Promise} the api response
   */
  @action createUserInstitution = async ({
    institutionId,
    userId
  }) => {
    try {
      const apiUrl = `${Auth.ecms}${USER_ENDPOINTS.CREATE_USER_INSTITUTION}`;
      const response = await Auth.fetch(apiUrl, {
        body: { institutionId, userId },
        method: 'POST'
      });
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Remove an institution from a user.
   * @param {{ institutionId: string, userId: string }} args
   * @returns {Promise} the api response
   */
  @action removeUserInstitution = async ({
    institutionId,
    userId
  }) => {
    try {
      const apiUrl = `${Auth.ecms}${USER_ENDPOINTS.REMOVE_USER_INSTITUTION}`;
      const response = await Auth.fetch(apiUrl, {
        body: { institutionId, userId },
        method: 'POST'
      });
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  // TODO disabled on the satellite for now (publisher-wide; retained for possible future DA flows).
  // TODO See AdminUserProfile.js for the calling-side rationale and c2c-support
  // TODO (UserInstitutions.jsx + AddInstitutionDialog.jsx) for the original.
  /**
   * Search all institutions by name across the publisher.
   * @param {{ searchText?: string }} [args]
   * @returns {Promise<Array>} the matching institutions (empty array on failure)
   */
  @action fetchUserAssignablePublisherInstitutions = async (_args = {}) => {
    console.warn('userManager.fetchUserAssignablePublisherInstitutions: not implemented yet');
    return [];
    // const { searchText = '' } = _args;
    // try {
    //   let apiUrl = `${Auth.ecms}${USER_ENDPOINTS.VIEW_INSTITUTIONS_FOR_DROPDOWN}`;
    //   apiUrl += '?skip=0&page=1&pageSize=100';
    //   apiUrl += '&filter[logic]=and';
    //   apiUrl += '&filter[filters][0][field]=name';
    //   apiUrl += '&filter[filters][0][operator]=contains';
    //   apiUrl += `&filter[filters][0][value]=${encodeURIComponent(searchText)}`;
    //   apiUrl += '&filter[filters][0][ignoreCase]=true';
    //   apiUrl += `&_=${Date.now()}`;
    //   const response = await Auth.fetch(apiUrl, { method: 'GET' });
    //   if (response?.status === 'SUCCESS') {
    //     return response.data || [];
    //   }
    //   return [];
    // } catch (error) {
    //   console.error(error);
    //   return [];
    // }
  }

  /**
   * Search school institutions by name within a single district.
   * @param {{ districtId: string, searchText?: string }} [args]
   * @returns {Promise<Array>} the matching schools (empty array on failure)
   */
  @action fetchUserAssignableSchoolInstitutions = async ({ districtId, searchText = '' } = {}) => {
    try {
      let apiUrl = `${Auth.ecms}${USER_ENDPOINTS.VIEW_SCHOOL_INSTITUTIONS_FOR_DROPDOWN}`;
      apiUrl += `?districtId=${districtId}&skip=0&page=1&pageSize=100`;
      apiUrl += '&filter[logic]=and';
      apiUrl += '&filter[filters][0][field]=name';
      apiUrl += '&filter[filters][0][operator]=contains';
      apiUrl += `&filter[filters][0][value]=${encodeURIComponent(searchText)}`;
      apiUrl += '&filter[filters][0][ignoreCase]=true';
      apiUrl += `&_=${Date.now()}`;
      const response = await Auth.fetch(apiUrl, { method: 'GET' });
      if (response?.status === 'SUCCESS') {
        return response.data || [];
      }
      return [];
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  /**
   * Add the given institutions to the local `institutionsMap` cache.
   * @param {Array} [institutions]
   */
  @action addUserInstitutionsToMap = (institutions = []) => {
    for (const institution of institutions) {
      this.institutionsMap.set(institution.id, institution);
    }
  }

  /**
   * Atomically replace `institutionsMap` with the given institutions.
   * @param {Array} [institutions]
   */
  @action replaceUserInstitutionsMap = (institutions = []) => {
    this.institutionsMap.clear();
    this.addUserInstitutionsToMap(institutions);
  }

  /**
   * @param {string} institutionId
   * @param {import('../services/UserInstitutionDropdownConstants').InstitutionDropDownMode} mode
   */
  @action setSelectedDropdownInstitutionId = (institutionId, mode = INSTITUTION_DROPDOWN_MODE.ALL_MODES) => {
    const { ADD_CLASSROOM_MODE, ALL_MODES, SELECT_CLASSROOM_MODE, SELECT_REPORT_CLASSROOM_MODE } = INSTITUTION_DROPDOWN_MODE;
    if (mode === ALL_MODES) {
      this.selectedDropdownInstitutionIdObservableObj[ADD_CLASSROOM_MODE] = institutionId;
      this.selectedDropdownInstitutionIdObservableObj[SELECT_CLASSROOM_MODE] = institutionId;
      this.selectedDropdownInstitutionIdObservableObj[SELECT_REPORT_CLASSROOM_MODE] = institutionId;
    } else {
      this.selectedDropdownInstitutionIdObservableObj[mode] = institutionId;
    }
    setSessionStorageItem('selectedDropdownInstitutionIdStr', institutionId);
  }

  /**
   * Fetch the logged in user's primary institution image and store it.
   * @returns {Promise<null|false>}
   */
  fetchInstitutionImage = async () => {
    if (this.userId !== null && this.userId !== undefined && this.userId !== '') {
      try {
        const response = await Auth.fetch(`${Auth.ecms}/api/viewMyInstitution`, {
          method: 'GET'
        });
        if (response && response.status && response.status === 'SUCCESS') {
          const institution = response.data[0];
          if (institution !== null && institution !== undefined) {
            this.setInstitutionImage(institution.imageContentItemId);
          }
        }
      } catch (err) {
        console.error(err);
        return false;
      }
    }
    return null;
  }

  /**
   * @param {string} image the institution image content item id
   */
  @action setInstitutionImage = (image) => {
    this.institutionImage = image;
  }

  getUserInvitation = async (userId) => {
    try {
      let apiUrl = `${Auth.ecms}${USER_ENDPOINTS.GET_USER_INVITATION}`;
      apiUrl += `?id=${userId}`;
      const response = await Auth.fetch(apiUrl, { method: 'GET' });
      if (response?.status === 'SUCCESS') {
        return response;
      }
      return false;
    } catch (error) {
      console.error(error);
    }
  }

  activateUserInvitation = async (userId, firstName, lastName, password, authKey) => {
    try {
      const body = {
        id: userId,
        firstName,
        lastName,
        password,
        authKey
      };
      const apiUrl = `${Auth.ecms}${USER_ENDPOINTS.ACTIVATE_USER_INVITATION}`;

      const response = await Auth.fetch(apiUrl, {
        body,
        method: 'POST'
      });

      if (response?.status === 'SUCCESS') {
        return true;
      }
      return false;
    } catch (error) {
      console.error(error);
    }
  }

  @action uploadImage = async (formData) => {
    const token = Auth.getToken();

    const headers = new Headers({
      Authorization: token,
      Accept: 'application/json; text/plain'
    });

    try {
      const response = await fetch(`${Auth.ecms}/api/saveUserProfileImage?saveUserProfile=false`, {
        method: 'POST',
        body: formData,
        headers

      });
      const data = await response.json();
      return data.newFileImageUrl;
    } catch (err) {
      console.error(err);
      return false;
    }
  }

  verifyUser = async (verificationId) => {
    try {
      const result = await Auth.fetch(`${Auth.ecms}/api/verifyUser?id=${verificationId}`, {
        method: 'GET'
      });

      if (result && result.status && result.status === 'SUCCESS') {
        return true;
      }
      return false;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  @action async setProfileImageUrl(newFileName, save, userId = null) {
    const now = new Date();
    this.profileImageUrl = newFileName;

    const tempArray = this.fullProfileImageUrl.split('resourceFileName=');
    if (!tempArray || !tempArray[0]) {
      this.fullProfileImageUrl = newFileName;
    } else {
      this.fullProfileImageUrl = `${tempArray[0]}resourceFileName=${newFileName}&authKey=${this.authKey}&rn=${now.getTime()}`;
    }
    if (save) {
      await this.saveImage(newFileName, userId);
    }
  }

  getFullUrlFromName = (imageName, { profileImageUrl } = {}) => {
    const fullProfileImageUrl = profileImageUrl || this.fullProfileImageUrl;

    const resourceFileNameParamPrefix = fullProfileImageUrl?.includes?.('resourceFileName=') ? 'resourceFileName=' : 'fileName=';

    const now = new Date();
    if (!imageName || !fullProfileImageUrl) {
      return null;
    }
    const tempArray = fullProfileImageUrl.split(resourceFileNameParamPrefix);
    if (!tempArray || !tempArray[0]) {
      return imageName;
    }
    return `${tempArray[0]}${resourceFileNameParamPrefix}${imageName}&authKey=${this.authKey}&rn=${now.getTime()}`;
  }

  @action saveUserDefaultAvatar = async (selectedAvatarName, userId = null) => {
    try {
      const body = {
        profileImage: `85px-${selectedAvatarName}.png`,
        userType: this.isStudent ? 'student' : 'teacher',
        ...(userId ? { userId } : {})
      };
      const apiUrl = `${Auth.ecms}${USER_ENDPOINTS.SAVE_USER_DEFAULT_AVATAR}`;

      const response = await Auth.fetch(apiUrl, {
        body,
        method: 'POST'
      });

      if (response && response.status === 'SUCCESS') {
        const save = false;
        await this.setProfileImageUrl(response.imageName, save, userId);
        return response.imageName;
      }
    } catch (error) {
      console.error(error);
    }
  }

  submitCrop = async (newFileName, crop) => {
    const body = {
      x: String(crop.x),
      y: String(crop.y),
      toX: String(crop.x), // not used in backend
      toY: String(crop.y), // not used in backend
      width: String(crop.width),
      height: String(crop.height),
      fileName: String((newFileName) || this.profileImageUrl)
    };
    try {
      const response = await Auth.fetch(`${Auth.ecms}/api/cropImagesForImagePicker?imageFromResources=false&folderName=avatars`, {
        method: 'POST',
        body
      });
      if (response && response.status === 'SUCCESS') {
        return response.newFileName;
      }
      return null;
    } catch (e) {
      console.error(e);
    }
  }

  getFullProfileImageUrl = (imageUrl) => {
    const now = new Date();
    if (imageUrl !== null && imageUrl !== undefined && imageUrl != '') {
      return `${Auth.ecms}/api/redirectToResource?resourceFolder=avatars&resourceFileName=${imageUrl}&authKey=${Auth.authKey}&rn=${now.getTime()}`;
    }
    return null;
  }

  @action updatePassword = async (oldPassword, newConfirmPassword) => {
    const body = {
      oldPassword,
      newPassword: newConfirmPassword
    };

    let UPDATE_PROFILE_PASSWORD;
    if (this.isStudent) {
      UPDATE_PROFILE_PASSWORD = typeof this.customEndpointsForUser?.UPDATE_PROFILE_PASSWORD_STUDENT === 'string' ? (
        this.customEndpointsForUser.UPDATE_PROFILE_PASSWORD_STUDENT
      ) : USER_ENDPOINTS.UPDATE_PROFILE_PASSWORD_STUDENT;
    } else {
      UPDATE_PROFILE_PASSWORD = typeof this.customEndpointsForUser?.UPDATE_PROFILE_PASSWORD === 'string' ? (
        this.customEndpointsForUser.UPDATE_PROFILE_PASSWORD
      ) : USER_ENDPOINTS.UPDATE_PROFILE_PASSWORD;
    }

    try {
      const result = await Auth.fetch(`${Auth.ecms}${UPDATE_PROFILE_PASSWORD}`, {
        method: 'POST',
        body
      });
      if (result && result.status === 'SUCCESS') {
        return true;
      }
      return false;
    } catch (err) {
      console.error(err);
      return false;
    }
  }

  @action saveChanges = async (input) => {
    try {
      const body = {
        userId: input.userId,
        firstName: input.firstName,
        lastName: input.lastName
      };

      if (input.enabled != undefined) {
        body.enabled = input.enabled;
      }

      if (input.timezoneId) {
        body.timezoneId = input.timezoneId;
      }

      if (input.username) {
        body.username = input.username;
      }

      if (input.id) {
        body.id = input.id;
      }

      if (input.password) {
        body.password = input.password;
      }

      const data = await Auth.fetch(`${Auth.ecms}/api/updateUserProfile`, {
        method: 'POST',
        body
      });

      if (data && data.status === 'SUCCESS') {
        this.setFirstName(body.firstName);
        this.setLastName(body.lastName);
        return true;
      }
      return false;
    } catch (err) {
      console.error(err);
      return false;
    }
  }

  @action enableUser = async (userId) => {
    try {
      const body = {
        userId
      };

      const data = await Auth.fetch(`${Auth.ecms}/api/activateUser`, {
        method: 'POST',
        body
      });

      if (data && data.status === 'SUCCESS') {
        this.setUserEnabled(true);
        return true;
      }
      return false;
    } catch (err) {
      console.error(err);
      return false;
    }
  }

  saveImage = async (imageName, userId = null) => {
    try {
      const body = {
        profileImage: imageName
      };
      if (userId) {
        body.userId = userId;
      }
      const data = await Auth.fetch(`${Auth.ecms}/api/updateUserProfile`, {
        method: 'POST',
        body
      });
      if (data && data.status === 'SUCCESS') {
        return true;
      }
      return false;
    } catch (err) {
      console.error(err);
      return false;
    }
  }

  @action resetPassword = async (passwordChangeId, password) => {
    let response = null;
    try {
      const CHANGE_FORGOTTEN_PASSWORD = typeof this.customEndpointsForUser?.CHANGE_FORGOTTEN_PASSWORD === 'string' ? (
        this.customEndpointsForUser.CHANGE_FORGOTTEN_PASSWORD
      ) : USER_ENDPOINTS.CHANGE_FORGOTTEN_PASSWORD;

      response = await Auth.fetch(`${Auth.ecms}${CHANGE_FORGOTTEN_PASSWORD}?passwordChangeId=${passwordChangeId}&password=${password}`, {
        method: 'POST'
      });
    } catch (e) {
      console.error(e);
      return false;
    }

    if (response && response.status === 'SUCCESS') {
      return true;
    }
  }

  @action sendResetEmail = async (email) => {
    let response = null;
    try {
      response = await Auth.fetch(`${Auth.ecms}/api/sendResetPasswordEmail?publisherSatelliteCode=${Auth.publisherSatelliteCode}&emailAddress=${email}`, {
        method: 'POST'
      });
    } catch (e) {
      console.error(e);
      return false;
    }

    if (response && response.status === 'SUCCESS') {
      if (response.emailSent === false) {
        return false;
      }
      return true;
    }
    return false;
  }

  @action setCustomEndpointsForUser = (customEndpointsForUser) => {
    if (typeof customEndpointsForUser === 'object') {
      this.customEndpointsForUser = customEndpointsForUser;
    }
  }

  validateAccessCode = async (accessCode) => {
    try {
      const data = await Auth.fetch(`${Auth.ecms}/api/validateSignUpCode`, {
        method: 'POST',
        body: {
          signupCode: accessCode.value,
          publisherSatelliteCode: Auth.publisherSatelliteCode
        }
      });
      this.setAccessCodeResult(data);
      return true;
    } catch (error) {
      console.error(error);
    }
  }

  validateUser = async (accessCode, email) => {
    const { result } = accessCode;

    try {
      const data = await Auth.fetch(`${Auth.ecms}/api/checkEmailAddress`, {
        method: 'POST',
        body: { publisherId: result.publisherId, emailAddress: email.value }
      });

      this.setEmailResult(data);

      if (data.emailAddressExists) {
        email.error = ERROR_MESSAGES.USER_EXISTS;
      } else {
        email.error = null;
      }

      if (email.error !== null) {
        return false;
      }
      return true;
    } catch (error) {
      console.error(error);
    }
  }

  validateUserEmail = async (publisherId, emailAddress) => {
    try {
      const data = await Auth.fetch(`${Auth.ecms}/api/checkEmailAddress`, {
        method: 'POST',
        body: { publisherId, emailAddress }
      });

      if (data.emailAddressExists) {
        return true;
      }
      return false;
    } catch (error) {
      console.error(error);
    }
  }

  // Validates the student username.  If it's an email format, checks if satellite allows student email.
  validateStudentUsernameFormat = (username) => {
    let errorMessage = null;
    // First check if username has a value
    let valid = username && username.length > 0;
    if (valid) {
      // if so, check if username is email
      const emailRegexp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isEmail = emailRegexp.test(username);
      if (isEmail && !this.allowStudentUsernamesAsEmail) {
        valid = false;
        errorMessage = ERROR_MESSAGES.STUDENT_USERNAME_INVALID_EMAIL;
      }
    } else {
      errorMessage = ERROR_MESSAGES.VALID_EMAIL_FORMAT;
    }
    const usernameResult = {
      valid,
      errorMessage
    };
    return usernameResult;
  }

  updateStudentTextHelpOption = async (userId, textHelpEnabled) => {
    const body = {
      userId,
      textHelpEnabled
    };
    try {
      const response = await Auth.fetch(`${Auth.ecms}/api/updateStudentTextHelpOption`, {
        method: 'POST',
        body
      });
      if (response && response.status === 'SUCCESS') {
        return true;
      }
      return false;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  updateMyTextHelpOption = async (textHelpEnabled) => {
    const body = {
      textHelpEnabled
    };
    try {
      const response = await Auth.fetch(`${Auth.ecms}/api/updateMyTextHelpOption`, {
        method: 'POST',
        body
      });
      if (response && response.status === 'SUCCESS') {
        return true;
      }
      return false;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  registerUser = async (body, accessCode) => {
    let url = Auth.ecms;
    // determine which signup url to call
    if (accessCode) {
      if (accessCode.codeType === 'district' || accessCode.codeType === 'school') {
        switch (accessCode.codeCategory) {
          case 'district_admin':
            url += USER_ENDPOINTS.SIGNUP_DISTRICT_ADMIN;
            break;
          case 'school_admin':
            url += USER_ENDPOINTS.SIGNUP_SCHOOL_ADMIN;
            break;
          case 'user':
            url += USER_ENDPOINTS.SIGNUP_TEACHER;
            break;
          default:
        }
      } else if (accessCode.codeType === 'classroom') {
        switch (accessCode.codeCategory) {
          case 'classroom':
            const SIGNUP_STUDENT = typeof this.customEndpointsForUser?.SIGNUP_STUDENT === 'string' ? (
              this.customEndpointsForUser.SIGNUP_STUDENT
            ) : USER_ENDPOINTS.SIGNUP_STUDENT;
            url += SIGNUP_STUDENT;
            break;
          case 'coteacher':
            url += USER_ENDPOINTS.SIGNUP_TEACHER;
            break;
          default:
        }
      }
    } else if (!accessCode && body.userType === 'teacher') {
      // special case for admin adding a teacher user
      url += USER_ENDPOINTS.SIGNUP_TEACHER;
    }

    try {
      const data = await Auth.fetch(url, {
        method: 'POST',
        body
      });
      // if the status is SUCCESS and there is an ID then new user registered
      if (data.status === 'SUCCESS' && data.userId) {
        return true;
      }
      return false;
    } catch (error) {
      console.error(error);
    }
  }

  registerDemolinkUser = async ({ demolinkId, firstName, lastName } = {}) => {
    try {
      const apiUrl = `${Auth.ecms}${USER_ENDPOINTS.REGISTER_DEMOLINK_USER}`;
      const result = await Auth.fetch(apiUrl, {
        body: {
          code: demolinkId,
          firstName,
          lastName,
          publisherSatelliteCode: Auth.publisherSatelliteCode
        },
        method: 'POST'
      });
      if (result.status !== 'SUCCESS') {
        console.error(result);
      }
      return result;
    } catch (error) {
      console.error(error);
      return error;
    }
  }

  registerEcommerceUser = async (body) => {
    try {
      let endpoint;
      // TODO - once KYA is ready to exclusively use the shopify implementation (and the `/activation` route),
      // TODO - we will remove the call to `/api/generateEcommerceUser` (REGISTER_ECOMMERCE_USER) (and the `/activate` route)
      if (window?.location?.pathname?.includes?.('/activate')) {
        endpoint = USER_ENDPOINTS.REGISTER_ECOMMERCE_USER;
      } else {
        endpoint = USER_ENDPOINTS.ACTIVATE_SHOPIFY_SIGNUP_CODE;
      }
      const apiUrl = `${Auth.ecms}${endpoint}`;
      const result = await Auth.fetch(apiUrl, {
        body,
        method: 'POST'
      });
      return result;
    } catch (error) {
      console.error(error);
    }
  }

  trackingInterval = null;

  async trackLoggedIn() {
    // avoid rogue interval if a new login happens before unauthorized
    if (this.trackingInterval) {
      clearInterval(this.trackingInterval);
    }

    this.trackingInterval = setInterval(async () => {
      this.pingLoggedIn();
    }, 60000);
  }

  pingLoggedIn = async () => {
    try {
      const response = await Auth.fetch(`${Auth.ecms}${USER_ENDPOINTS.PING_LOGGED_IN}`, {
        method: 'POST'
      });
      if (response && response.status === 'SUCCESS') {
        this.pingErrorCount = 0;
        return true;
      }
      return false;
    } catch (error) {
      console.error(error);
      if (error.message === 'Unauthorized' || error.message.includes('NetworkError') || error.message.includes('Failed to fetch') || (
        error.message.includes('not valid')
      )) {
        this.pingErrorCount++;
        if (this.pingErrorCount > 3) {
          const InitService = getRegisteredClass('InitService');

          clearInterval(this.trackingInterval);

          InitService.logoutFromPingError();
          // TODO remove // Auth.logout();
          // TODO remove // window.location = '/signin';
          return false;
        }
      }
      return true;
    }
  }

  // ============================================================================
  // PREVIEW MODE HELPERS
  // ============================================================================

  /**
   * Returns true if user is a publisher actively previewing content as teacher/student.
   *
   * Note: This is similar to `productManager.isFromProduct` but more specific:
   * - `isFromProduct` indicates the user is in product navigation context (broader)
   * - `isInPublisherPreviewMode` indicates active preview permission is set (narrower)
   *
   * Use this when you need to specifically check if a publisher user has activated
   * preview mode (as opposed to just browsing products).
   */
  @computed get isInPublisherPreviewMode() {
    return (this.hasPublisherPermission && !!this.activePreviewPermissionId);
  }

  /**
   * Returns true if user is previewing as a teacher (e.g. publisher user previewing course as teacher).
   * This is specifically for publisher course preview mode, not general teacher access.
   */
  @computed get isTeacherPreview() {
    return (this.activePreviewPermissionId === this.teacherPermissionId);
  }

  /**
   * Returns true if user is previewing as a student (e.g. publisher user previewing course as student).
   * This is specifically for publisher course preview mode, not general student access.
   */
  @computed get isStudentPreview() {
    return (this.activePreviewPermissionId === this.studentPermissionId);
  }

  /**
   * Returns true if user is a teacher OR a publisher user previewing a course as teacher.
   * Use this for UI elements that should be visible to both real teachers and publisher preview users.
   * For API calls and permission checks, use the strict `isTeacher` instead.
   */
  @computed get isTeacherOrTeacherPreview() {
    return (
      (this.activePermissionId === this.teacherPermissionId) ||
      (this.activePreviewPermissionId === this.teacherPermissionId) ||
      this.isLplAdmin
    );
  }

  /**
   * Returns true if user is a student OR a publisher user previewing a course as student.
   * Use this for UI elements that should be visible to both real students and publisher preview users.
   * For API calls and permission checks, use the strict `isStudent` instead.
   */
  @computed get isStudentOrStudentPreview() {
    return (
      (this.activePermissionId === this.studentPermissionId) ||
      (this.activePreviewPermissionId === this.studentPermissionId)
    );
  }

  // ============================================================================
  // ROLES (`activePermissionId` determines the current satellite 'role')
  // ============================================================================

  @computed get isDistrictOrSchoolAdmin() {
    return this.activePermissionId === this.districtAdminId || this.activePermissionId === this.schoolAdminId;
  }

  @computed get isDistrictAdmin() {
    return this.activePermissionId === this.districtAdminId;
  }

  @computed get isSchoolAdmin() {
    return this.activePermissionId === this.schoolAdminId;
  }

  @computed get isDistrictOrSchoolAssessmentAdmin() {
    return this.activePermissionId === this.districtAssessmentAdminId || this.activePermissionId === this.schoolAssessmentAdminId;
  }

  @computed get isDistrictAssessmentAdmin() {
    return this.activePermissionId === this.districtAssessmentAdminId;
  }

  @computed get isSchoolAssessmentAdmin() {
    return this.activePermissionId === this.schoolAssessmentAdminId;
  }

  @computed get isProductAdmin() {
    return this.activePermissionId === this.districtProductAdminId || this.activePermissionId === this.schoolProductAdminId;
  }

  @computed get isDistrictProductAdmin() {
    return this.activePermissionId === this.districtProductAdminId;
  }

  @computed get isSchoolProductAdmin() {
    return this.activePermissionId === this.schoolProductAdminId;
  }

  /**
   * Returns true if user's active permission is teacher (does NOT include preview).
   * This is the strict check used for API calls and permission validation.
   * For UI that should show in preview mode, use `isTeacherOrTeacherPreview` instead.
   */
  @computed get isTeacher() {
    return (this.activePermissionId === this.teacherPermissionId) || this.isLplAdmin;
  }

  /**
   * Returns true if user's active permission is student (does NOT include preview).
   * This is the strict check used for API calls and permission validation.
   * For UI that should show in preview mode, use `isStudentOrStudentPreview` instead.
   */
  @computed get isStudent() {
    return (this.activePermissionId === this.studentPermissionId);
  }

  @computed get isLicensedBySite() {
    const UserService = getRegisteredClass('UserService');
    const selectedInstitution = UserService.getCurrentInstitutionForLoggedInUser({
      useDefaultInstitutionIfNoneSelected: false
    });
    if (this.isTeacher && selectedInstitution) {
      return selectedInstitution?.licenseModel === 'SITE_LICENSE';
    } else {
      return !!this.institutions?.find((institution) => {
        return institution?.licenseModel === 'SITE_LICENSE';
      });
    }
  }

  @computed get isLicensedByPurchaseCode() {
    const UserService = getRegisteredClass('UserService');
    const selectedInstitution = UserService.getCurrentInstitutionForLoggedInUser({
      useDefaultInstitutionIfNoneSelected: false
    });
    if (this.isTeacher && selectedInstitution) {
      return selectedInstitution?.licenseModel === 'PURCHASE_CODE';
    } else {
      return !!this.institutions?.find((institution) => {
        return institution?.licenseModel === 'PURCHASE_CODE';
      });
    }
  }

  @computed get licenseInstruction() {
    return this.institutions?.find((institution) => {
      return institution?.licenseInstruction;
    })?.licenseInstruction;
  }

  @computed get licenseInstructionUrl() {
    return this.institutions?.find((institution) => {
      return institution?.licenseInstructionUrl;
    })?.licenseInstructionUrl;
  }

  // ============================================================================
  // PERMISSIONS
  // ============================================================================

  @computed get hasPublisherPermission() {
    return this.loaded && (
      this.permissionIds.includes(this.publisherAdminId) ||
      this.permissionIds.includes(this.publisherUserId)
    );
  }

  @computed get hasDistrictAdminPermission() {
    return this.loaded && this.permissionIds.includes(this.districtAdminId);
  }

  @computed get hasDistrictAssessmentAdminPermission() {
    return this.loaded && this.permissionIds.includes(this.districtAssessmentAdminId);
  }

  @computed get hasSchoolAdminPermission() {
    return this.loaded && this.permissionIds.includes(this.schoolAdminId);
  }

  @computed get hasSchoolAssessmentAdminPermission() {
    return this.loaded && this.permissionIds.includes(this.schoolAssessmentAdminId);
  }

  @computed get hasProductAdminPermission() {
    return this.hasDistrictProductAdminPermission || this.hasSchoolProductAdminPermission;
  }

  @computed get hasDistrictProductAdminPermission() {
    return this.loaded && this.permissionIds.includes(this.districtProductAdminId);
  }

  @computed get hasSchoolProductAdminPermission() {
    return this.loaded && this.permissionIds.includes(this.schoolProductAdminId);
  }

  @computed get hasTeacherPermission() {
    return this.loaded && this.permissionIds.includes(this.teacherPermissionId);
  }

  @computed get hasStudentPermission() {
    return this.loaded && this.permissionIds.includes(this.studentPermissionId);
  }

  @computed get hasSatelliteBetaAccessUserPermission() {
    return this.loaded && this.permissionIds.includes(this.satelliteBetaAccessUserId);
  }

  @computed get hasExperimentalAccessPermission() {
    return this.loaded && this.permissionIds.includes(this.experimentalAccessId);
  }

  @computed get hasManageUploadedFilesPermission() {
    return this.loaded && this.permissionIds.includes(this.manageUploadedFilesId);
  }

  @computed get hasViewEngagementDataPermission() {
    return this.loaded && this.permissionIds.includes(this.viewEngagementDataId);
  }

  @computed get hasViewWhiteLabelStandardsReportsPermission() {
    return this.loaded && this.permissionIds.includes(this.viewWhiteLabelStandardsReportsId);
  }

  /**
   * Edge case for LPL Admin - custom variation of a teacher user.
   * Currently only used by Empowered satellite.
   * Returns true only if satellite has enabled this via `setAllowLplAdmin(true)` and user has 'satellite_admin_user' permission.
   */
  @computed get isLplAdmin() {
    return this.loaded && this.allowLplAdmin && this.permissionIds.includes(this.satelliteAdminId);
  }

  @computed get canViewAsTeacher() {
    return (this.loaded && (this.permissionIds.includes(this.teacherPermissionId) ||
      this.permissionIds.includes(this.satelliteAdminId) ||
      this.permissionIds.includes(this.districtAdminId) ||
      this.permissionIds.includes(this.schoolAdminId) ||
      this.permissionIds.includes(this.publisherAdminId) ||
      this.permissionIds.includes(this.publisherUserId) ||
      this.permissionIds.includes(this.districtProductAdminId) ||
      this.permissionIds.includes(this.schoolProductAdminId))
    );
  }

  @computed get hasMultipleInstitutions() {
    return this.institutionsMap.size > 1;
  }

  @computed get institutionDropdownOptions() {
    const keys = this.institutionIds;
    const options = keys.map((institutionId) => {
      const institution = this.institutionsMap.get(institutionId);
      return {
        text: institution.name,
        value: institution.id,
        type: institution.type
      };
    }).sort((a, b) => a.text?.localeCompare(b.text, 'en', { numeric: true }));
    return options;
  }

  @computed get institutionIds() {
    return Array.from(this.institutionsMap.values())
      .sort((a, b) => {
        return (a.name || '').localeCompare(b.name || '', 'en', { numeric: true });
      })
      .map((institution) => institution.id);
  }

  @computed get institutions() {
    return Array.from(this.institutionsMap.values()).sort((a, b) => {
      return a.name?.localeCompare(b.name, 'en', { numeric: true });
    });
  }

  @computed get canManageLearnosityScores() {
    return (this.loaded && this.permissionIds.includes(this.manageLearnosityScoresId));
  }

  @computed get canViewSatelliteTestBuilder() {
    if (!this.loaded) {
      return false;
    }
    if (this.isDistrictOrSchoolAdmin || this.isDistrictOrSchoolAssessmentAdmin) {
      return (
        this.permissionIds.includes(this.viewAdminLibraryItemBank) || this.permissionIds.includes(this.viewAdminLibraryResourceBank)
      );
    } else if (this.isTeacher) {
      return (
        this.permissionIds.includes(this.viewTeacherLibraryItemBank) || this.permissionIds.includes(this.viewTeacherLibraryResourceBank)
      );
    } else {
      return false;
    }
  }

  @computed get canViewSatelliteContent() {
    return (this.loaded && this.permissionIds.includes(this.viewSatelliteContent));
  }

  @computed get canViewSatelliteProducts() {
    return (this.loaded && !this.isDistrictOrSchoolAssessmentAdmin && this.allowViewSatelliteProductTab);
  }

  @computed get hasViewAdminOrTeacherLibraryItemBankPermission() {
    return (this.loaded && (
      (this.permissionIds.includes(this.viewAdminLibraryItemBank) || this.permissionIds.includes(this.viewTeacherLibraryItemBank))
    ));
  }

  @computed get hasViewAdminOrTeacherLibraryResourceBankPermission() {
    return (this.loaded && (
      (this.permissionIds.includes(this.viewAdminLibraryResourceBank) || this.permissionIds.includes(this.viewTeacherLibraryResourceBank))
    ));
  }

  @computed get canViewAdminLibraryItemBank() {
    if (!this.loaded) {
      return false;
    }
    if (this.isDistrictOrSchoolAdmin || this.isDistrictOrSchoolAssessmentAdmin || this.isProductAdmin) {
      return this.permissionIds.includes(this.viewAdminLibraryItemBank);
    } else {
      return false;
    }
  }

  @computed get canViewAdminLibraryResourceBank() {
    if (!this.loaded) {
      return false;
    }
    if (this.isDistrictOrSchoolAdmin || this.isDistrictOrSchoolAssessmentAdmin || this.isProductAdmin) {
      return this.permissionIds.includes(this.viewTeacherLibraryResourceBank);
    } else {
      return false;
    }
  }

  @computed get canViewTeacherLibraryItemBank() {
    if (!this.loaded) {
      return false;
    }
    if (this.isDistrictOrSchoolAdmin || this.isDistrictOrSchoolAssessmentAdmin || this.isProductAdmin) {
      return this.permissionIds.includes(this.viewTeacherLibraryItemBank);
    } else {
      return false;
    }
  }

  @computed get canViewTeacherLibraryResourceBank() {
    if (!this.loaded) {
      return false;
    }
    if (this.isDistrictOrSchoolAdmin || this.isDistrictOrSchoolAssessmentAdmin || this.isProductAdmin) {
      return this.permissionIds.includes(this.viewTeacherLibraryResourceBank);
    } else {
      return false;
    }
  }

  @computed get canViewSchoolsAsAdmin() {
    return (this.loaded && (this.isDistrictOrSchoolAdmin || this.isProductAdmin) && (
      this.permissionIds.includes(this.viewSatelliteSchools)
    ));
  }

  @computed get canViewUsersAsAdmin() {
    return (this.loaded && this.isDistrictOrSchoolAdmin && (
      this.permissionIds.includes(this.viewSatelliteUsers)
    ));
  }

  @computed get canViewClassroomsAsAdmin() {
    return (this.loaded && this.isDistrictOrSchoolAdmin && (
      this.permissionIds.includes(this.viewSatelliteClasses)
    ));
  }

  @computed get canViewReportsAsAdmin() {
    return (this.loaded && this.isDistrictOrSchoolAdmin && (
      this.permissionIds.includes(this.viewSatelliteReports)
    ) && !this.hideReportsTab);
  }

  @computed get canViewLiveTestBuilderContent() {
    return (this.loaded && this.permissionIds.includes(this.viewSatelliteLive));
  }
}

export default new UserManager();
